import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REGISTER_ORDINAL_MAX, REGISTER_ORDINAL_MIN } from '@dashboard/contracts';
import { apiRequest, setCsrfToken } from '../api/client';
import { useAuthStatus } from '../api/queries';
import {
  enqueue,
  newClientRef,
  readQueue,
  recordFailure,
  removeFromQueue,
  type QueuedObservation,
} from './observation-queue';
import { todayIsoDate } from '../i18n';

/**
 * Writing down what you just saw, from a phone, with or without signal.
 *
 * ## Why this renders before the session does
 *
 * Everywhere else in the application, a failed `/auth/context` is an error
 * screen -- correctly, since there is nothing to show. Here it is the ordinary
 * case: somebody is standing next to a hazard with no bars, and an error screen
 * would lose the note they came to write. So `App` renders this route before
 * the auth gate, and the screen works from what the device already knows.
 *
 * Nothing is *sent* without a session. Queuing needs no authority; filing does,
 * and filing only happens when there is network, which is when the session is
 * available again.
 *
 * ## Why the project is remembered rather than fetched
 *
 * The project list is an API read, and the API is the thing that is missing.
 * So the last project this device saw is kept locally and used offline. A
 * device that has never been online here has nothing to attach an observation
 * to, and says so rather than filing into a guess.
 */

const PROJECT_KEY = 'dashboard.capture.project';

interface RememberedProject {
  id: string;
  name: string;
}

/** What the device last knew, for a session it may not be able to load. */
export function rememberProject(project: RememberedProject): void {
  try {
    window.localStorage?.setItem(PROJECT_KEY, JSON.stringify(project));
  } catch {
    /* Storage refused. Capture will say it has no project, which is true of
       this device and is a better answer than filing into a guess. */
  }
}

function rememberedProject(): RememberedProject | null {
  try {
    const raw = window.localStorage?.getItem(PROJECT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    return typeof record.id === 'string' && typeof record.name === 'string'
      ? { id: record.id, name: record.name }
      : null;
  } catch {
    return null;
  }
}

/**
 * How long the drain waits for a session before sending without one.
 *
 * Long enough that a connection which has genuinely just come back returns a
 * token first, short enough that a phone with no route out is not held up by
 * it. Nothing is lost by guessing low: a send with a stale token fails, records
 * its reason, and the next drain tries again with a fresh one.
 */
const SESSION_REFRESH_TIMEOUT_MS = 3_000;

/**
 * Resolves to `null` if the promise has not settled in time.
 *
 * Deliberately resolves rather than rejects: a timeout here is not an error to
 * report, it is the caller deciding to stop waiting. The distinction matters at
 * the call site, where a rejection means "asking failed" and null means "we did
 * not wait to find out" -- and both lead to the same next step of trying the
 * send anyway.
 */
export async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ms);
      }),
    ]);
  } finally {
    /* The loser of the race keeps running -- a paused query in particular never
       settles at all -- so the timer is cleared rather than left to fire into
       nothing. */
    if (timer !== undefined) clearTimeout(timer);
  }
}

export function CapturePage() {
  const { t } = useTranslation();
  const project = rememberedProject();

  const [queue, setQueue] = useState<QueuedObservation[]>(readQueue);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [sending, setSending] = useState(false);
  const [storageRefused, setStorageRefused] = useState(false);

  /*
   * A ref, not the state above.
   *
   * The drain is reached from a listener registered once, so it reads whatever
   * `sending` was when that closure was made -- which under StrictMode's double
   * mount is a value from a render that no longer exists. The first version
   * guarded on the state and never sent anything: every later drain saw a stale
   * `true` and returned. The state is still there because the button reads it;
   * the guard is this.
   */
  const inFlight = useRef(false);

  /*
   * The session, when there is one to have.
   *
   * This screen renders before the shell's own auth branch, so nothing has set
   * the CSRF token -- and a POST without one is refused. Asking here is what
   * makes the queue drainable at all. It fails offline, which is fine: the
   * queue simply waits, which is what it is for.
   */
  const status = useAuthStatus();
  useEffect(() => {
    if (status.data?.authenticated) setCsrfToken(status.data.context.csrfToken);
  }, [status.data]);

  /* Held in a ref because the drain runs from a listener whose closure is made
     once, and react-query does not promise a stable `refetch`. */
  const refreshSession = useRef(status.refetch);
  refreshSession.current = status.refetch;

  /**
   * Sends what it can, one at a time.
   *
   * Serially rather than in parallel: a queue is at most a shift's worth of
   * notes, and a phone coming back onto a weak connection does better with one
   * request than with six. A failure stops nothing -- the rest still try, and
   * the one that failed keeps its reason.
   */
  const drain = useCallback(async () => {
    if (inFlight.current) return;
    const pending = readQueue();
    if (pending.length === 0) return;

    inFlight.current = true;
    setSending(true);

    /*
     * The session first, always -- but never for longer than a moment.
     *
     * A phone that started up offline never loaded one, so it has no CSRF
     * token and every note in the queue would fail with `auth.csrf_invalid` --
     * which is what the browser test showed before this line existed. Asking
     * costs one request on a connection that has just come back, and it is the
     * difference between a queue that drains and one that reports the same
     * error forever.
     *
     * The wait is bounded because a refetch offline does not fail: react-query
     * defaults to `networkMode: 'online'`, which *pauses* a query while
     * `navigator.onLine` is false and leaves its promise unsettled until the
     * browser says the network is back. Awaiting that unbounded is the one way
     * this drain can hang -- the loop below never runs, no note records a
     * reason, and the screen becomes the queue that will not say why, which is
     * the single thing this module exists to prevent.
     *
     * So the flag is treated here as it is everywhere else on this screen: a
     * hint. Wait briefly for a token, then send with whatever we have. Sending
     * without a fresh one fails and records why, and a recorded reason beats
     * silence.
     */
    try {
      const refreshed = await withTimeout(refreshSession.current(), SESSION_REFRESH_TIMEOUT_MS);
      if (refreshed?.data?.authenticated) setCsrfToken(refreshed.data.context.csrfToken);
    } catch {
      /* Still no network. The sends below will fail and say so, which is the
         same outcome as skipping them and easier to explain. */
    }

    for (const entry of pending) {
      try {
        await apiRequest(`/api/v1/projects/${entry.projectId}/register`, {
          method: 'POST',
          body: {
            kind: 'observation',
            title: entry.title,
            description: entry.description || null,
            status: entry.status,
            severity: entry.severity,
            occurredOn: entry.observedOn,
            attributes: {
              location: entry.location || null,
              immediateAction: entry.immediateAction || null,
            },
            /* What makes the retry safe. A request that timed out may well have
               succeeded, and the server returns the first row for a reference
               it has already seen. */
            clientRef: entry.clientRef,
          },
        });
        removeFromQueue(entry.clientRef);
      } catch (error) {
        recordFailure(entry.clientRef, error instanceof Error ? error.message : String(error));
      }
    }
    setQueue(readQueue());
    inFlight.current = false;
    setSending(false);
  }, []);

  /* Drain when the browser says the network is back, and once on load: a phone
     that was closed offline and reopened in the depot never fires the event. */
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void drain();
    };
    const goOffline = () => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    /*
     * Tried on mount whatever `navigator.onLine` says.
     *
     * That flag is a hint and a famously unreliable one -- it reports "online"
     * behind a captive portal, on a Wi-Fi with no route out, and (as the
     * browser test for this screen showed) after a cold start with the radio
     * off. A wasted request costs one failed attempt and a recorded reason; a
     * skipped one costs somebody's note sitting on the phone while they
     * believe it has gone.
     */
    void drain();

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
    // Deliberately once: `drain` guards its own re-entry, and re-registering
    // the listeners on every send would drop events mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!project) {
    return (
      <main className="capture">
        <h1>{t('capture.title')}</h1>
        <p className="capture__note">{t('capture.noProject')}</p>
      </main>
    );
  }

  return (
    <main className="capture">
      <header className="capture__header">
        <h1>{t('capture.title')}</h1>
        <p className="capture__project">{project.name}</p>
        {/* The connection, stated rather than implied. Somebody who has just
            written down a hazard needs to know whether it has gone anywhere. */}
        <p className={online ? 'capture__status' : 'capture__status capture__status--offline'}>
          {online ? t('capture.online') : t('capture.offline')}
        </p>
      </header>

      <CaptureForm
        projectId={project.id}
        onQueued={(kept) => {
          setStorageRefused(!kept);
          setQueue(readQueue());
          void drain();
        }}
      />

      {storageRefused ? <p className="capture__error">{t('capture.storageRefused')}</p> : null}

      <QueueList queue={queue} sending={sending} onSend={() => void drain()} />
    </main>
  );
}

function CaptureForm({
  projectId,
  onQueued,
}: {
  projectId: string;
  onQueued: (kept: boolean) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const severityId = useId();
  const locationId = useId();
  const actionId = useId();
  const observedId = useId();

  const today = todayIsoDate();
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('');
  const [location, setLocation] = useState('');
  const [immediateAction, setImmediateAction] = useState('');
  const [observedOn, setObservedOn] = useState(today);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;

    const kept = enqueue({
      clientRef: newClientRef(),
      projectId,
      title: trimmed,
      description: '',
      severity: severity === '' ? null : Number(severity),
      location: location.trim(),
      immediateAction: immediateAction.trim(),
      observedOn,
      status: 'open',
      capturedAt: new Date().toISOString(),
      lastError: null,
    });

    /* Cleared whatever storage said. If the note was not kept the reader is
       told so above, and leaving the text in a form that claims to have saved
       it would be the worse of the two lies. */
    setTitle('');
    setSeverity('');
    setLocation('');
    setImmediateAction('');
    setObservedOn(today);
    onQueued(kept);
  };

  return (
    <form className="capture__form" onSubmit={submit}>
      {/* Title first and alone above the fold: the one field that must be
          filled, on a screen somebody is holding in one hand. */}
      <label className="capture__field" htmlFor={titleId}>
        <span>{t('capture.what')}</span>
        <input
          id={titleId}
          className="input"
          value={title}
          maxLength={200}
          required
          autoFocus
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>

      <label className="capture__field" htmlFor={locationId}>
        <span>{t('capture.where')}</span>
        <input
          id={locationId}
          className="input"
          value={location}
          maxLength={500}
          placeholder={t('capture.wherePlaceholder')}
          onChange={(event) => setLocation(event.target.value)}
        />
      </label>

      <label className="capture__field" htmlFor={severityId}>
        <span>{t('capture.severity')}</span>
        <select
          id={severityId}
          className="select"
          value={severity}
          onChange={(event) => setSeverity(event.target.value)}
        >
          {/* Not judged is a real answer, and the first one: somebody writing
              down what they saw is not always the person who rates it. */}
          <option value="">{t('capture.notJudged')}</option>
          {Array.from(
            { length: REGISTER_ORDINAL_MAX - REGISTER_ORDINAL_MIN + 1 },
            (_, index) => REGISTER_ORDINAL_MIN + index,
          ).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>

      <label className="capture__field" htmlFor={actionId}>
        <span>{t('capture.actionTaken')}</span>
        <textarea
          id={actionId}
          className="input"
          rows={3}
          value={immediateAction}
          maxLength={2000}
          onChange={(event) => setImmediateAction(event.target.value)}
        />
      </label>

      <label className="capture__field" htmlFor={observedId}>
        <span>{t('capture.observedOn')}</span>
        <input
          id={observedId}
          className="input"
          type="date"
          value={observedOn}
          onChange={(event) => setObservedOn(event.target.value)}
        />
      </label>

      <button
        type="submit"
        className="button button--primary capture__submit"
        disabled={!title.trim()}
      >
        {t('capture.save')}
      </button>
    </form>
  );
}

/** What is still on the device, and why. */
function QueueList({
  queue,
  sending,
  onSend,
}: {
  queue: readonly QueuedObservation[];
  sending: boolean;
  onSend: () => void;
}) {
  const { t } = useTranslation();

  if (queue.length === 0) {
    return <p className="capture__note">{t('capture.nothingWaiting')}</p>;
  }

  return (
    <section className="capture__queue" aria-label={t('capture.waiting')}>
      <h2>{t('capture.waitingCount', { count: queue.length })}</h2>
      <ul>
        {queue.map((entry) => (
          <li key={entry.clientRef}>
            <strong>{entry.title}</strong>
            {entry.location ? <span className="capture__where"> · {entry.location}</span> : null}
            {/* The reason, when there is one. A queue that is not draining and
                will not say why is the thing that makes people stop trusting
                it and go back to paper. */}
            {entry.lastError ? <span className="capture__error"> · {entry.lastError}</span> : null}
          </li>
        ))}
      </ul>
      {/* Never disabled for being "offline": that flag is a hint, and the
          moment a reader disagrees with it is exactly the moment they want to
          try. Only a send already in flight blocks another. */}
      <button type="button" className="button" disabled={sending} onClick={onSend}>
        {sending ? t('capture.sending') : t('capture.sendNow')}
      </button>
    </section>
  );
}
